import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../lib/apiError';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../../components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Search, 
  UserPlus, 
  MoreVertical, 
  Mail, 
  Key, 
  UserX, 
  UserCheck,
  PauseCircle,
  Loader2,
  Users,
  Shield,
  Activity,
  Clock,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import AdminUserDetailModal from '../../components/admin/AdminUserDetailModal';

const AdminUsersPage = () => {
  const { authApi, user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Create user modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState('');
  const [detailUserId, setDetailUserId] = useState('');
  const [newUser, setNewUser] = useState({
    email: '',
    role: 'subscriber',
    first_name: '',
    last_name: '',
    phone: '',
    country: '',
    city: '',
    assigned_region: '',
    performance_tier: 'Probation'
  });

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await authApi.get(`/admin/users?${params.toString()}`);
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to fetch users'));
    } finally {
      setLoading(false);
    }
  }, [authApi, page, roleFilter, statusFilter, searchQuery]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const requestedRole = searchParams.get('create');
    if (!requestedRole) return;
    setNewUser(prev => ({ ...prev, role: requestedRole }));
    setShowCreateModal(true);
  }, [searchParams]);

  const handleCreateModalOpen = (open) => {
    setShowCreateModal(open);
    if (!open && searchParams.has('create')) {
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      setSearchParams(next, { replace: true });
    }
  };

  // Create user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    
    try {
      await authApi.post('/admin/users', newUser);
      toast.success(`User created! Setup email sent to ${newUser.email}`);
      handleCreateModalOpen(false);
      setNewUser({
        email: '',
        role: 'subscriber',
        first_name: '',
        last_name: '',
        phone: '',
        country: '',
        city: '',
        assigned_region: '',
        performance_tier: 'Probation'
      });
      fetchUsers();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create user'));
    } finally {
      setCreateLoading(false);
    }
  };

  // Resend setup email
  const handleResendSetup = async (userId, email) => {
    try {
      await authApi.post(`/admin/users/${userId}/resend-setup`);
      toast.success(`Setup email resent to ${email}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to resend setup email'));
    }
  };

  // Force password reset
  const handleForcePasswordReset = async (userId, email) => {
    try {
      await authApi.post(`/admin/users/${userId}/force-password-reset`);
      toast.success(`Password reset email sent to ${email}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send reset email'));
    }
  };

  // Reset a dependant's password to the default (dependants log in by phone, no email)
  const handleResetDependantPassword = async (userId) => {
    try {
      const response = await authApi.post(`/admin/users/${userId}/reset-password`);
      const { loginIdentifier, temporaryPassword } = response.data || {};
      toast.success(
        `Password reset. Login: ${loginIdentifier || 'their phone'} — Password: ${temporaryPassword}`,
        { duration: 15000 }
      );
      fetchUsers();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to reset password'));
    }
  };

  // Deactivate user
  const handleDeactivate = async (userId) => {
    try {
      await authApi.post(`/admin/users/${userId}/deactivate`);
      toast.success('User deactivated');
      fetchUsers();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to deactivate user'));
    }
  };

  const handleSuspend = async (userId) => {
    try {
      await authApi.post(`/admin/users/${userId}/suspend`, { reason: 'Suspended by admin' });
      toast.success('User suspended');
      fetchUsers();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to suspend user'));
    }
  };

  // Reactivate user
  const handleReactivate = async (userId) => {
    try {
      await authApi.post(`/admin/users/${userId}/reactivate`);
      toast.success('User reactivated');
      fetchUsers();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to reactivate user'));
    }
  };

  const getDisplayName = (account) => {
    const fullName = [
      account?.profile?.first_name || account?.profile?.firstName,
      account?.profile?.last_name || account?.profile?.lastName
    ].filter(Boolean).join(' ').trim();
    return account?.display_name || fullName || account?.email || account?.phone || account?.contact || 'this user';
  };

  const getContactLabel = (account) => (
    account?.email || account?.phone || account?.phone_normalized || account?.contact || 'No email or phone listed'
  );

  const handleDeleteUser = async () => {
    if (!userToDelete?.id) return;
    const name = getDisplayName(userToDelete);
    setDeletingUserId(userToDelete.id);
    try {
      await authApi.delete(`/admin/users/${userToDelete.id}`, { params: { confirm: true } });
      toast.success(`${name} was permanently deleted`);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to permanently delete user'));
    } finally {
      setDeletingUserId('');
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-red-100 text-red-700 border-red-200',
      supervisor: 'bg-orange-100 text-orange-700 border-orange-200',
      care_officer: 'bg-blue-100 text-blue-700 border-blue-200',
      customer_care: 'bg-cyan-100 text-cyan-700 border-cyan-200',
      subscriber: 'bg-burgundy-100 text-burgundy-700 border-burgundy-200',
      dependant: 'bg-green-100 text-green-700 border-green-200'
    };
    const labels = {
      admin: 'Admin',
      supervisor: 'Supervisor',
      care_officer: 'Care Officer',
      customer_care: 'Customer Care',
      subscriber: 'Subscriber',
      dependant: 'Dependant'
    };
    return (
      <Badge className={`${styles[role] || 'bg-gray-100 text-gray-700'} border font-medium`}>
        {labels[role] || role}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700 border-green-200',
      pending_verification: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      pending_approval: 'bg-orange-100 text-orange-700 border-orange-200',
      suspended: 'bg-red-100 text-red-700 border-red-200',
      deactivated: 'bg-gray-100 text-gray-500 border-gray-200'
    };
    const labels = {
      active: 'Active',
      pending_verification: 'Pending Setup',
      pending_approval: 'Pending Approval',
      suspended: 'Suspended',
      deactivated: 'Deactivated'
    };
    return (
      <Badge className={`${styles[status] || 'bg-gray-100 text-gray-700'} border font-medium`}>
        {labels[status] || status}
      </Badge>
    );
  };

  // Stats
  const stats = {
    total: total,
    active: users.filter(u => u.status === 'active').length,
    pending: users.filter(u => u.status === 'pending_verification').length,
    deactivated: users.filter(u => u.status === 'deactivated').length
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">User Management</h1>
          <p className="text-gray-500 mt-1">Create and manage user accounts</p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-burgundy-600 hover:bg-burgundy-700 rounded-xl"
          data-testid="create-user-btn"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Create User
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-burgundy-100 rounded-lg">
              <Users className="h-5 w-5 text-burgundy-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-xs text-gray-500">Total Users</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Activity className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              <p className="text-xs text-gray-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              <p className="text-xs text-gray-500">Pending Setup</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Shield className="h-5 w-5 text-gray-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.deactivated}</p>
              <p className="text-xs text-gray-500">Deactivated</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by email or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl border-gray-200"
              data-testid="user-search-input"
            />
          </div>
          <Select value={roleFilter} onValueChange={(v) => setRoleFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-full sm:w-40 rounded-xl" data-testid="role-filter">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="subscriber">Subscriber</SelectItem>
              <SelectItem value="care_officer">Care Officer</SelectItem>
              <SelectItem value="customer_care">Customer Care</SelectItem>
              <SelectItem value="supervisor">Supervisor</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-full sm:w-40 rounded-xl" data-testid="status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="pending_verification">Pending Setup</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="deactivated">Deactivated</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-burgundy-600 mx-auto" />
            <p className="text-gray-500 mt-2">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">User</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Last Login</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors" data-testid={`user-row-${user.id}`}>
                    <td className="px-6 py-4">
                      <div>
                        <button
                          type="button"
                          onClick={() => setDetailUserId(user.id)}
                          className="text-left font-medium text-gray-900 hover:text-[#9F1D35]"
                        >
                          {getDisplayName(user)}
                        </button>
                        <p className="text-sm text-gray-500">{getContactLabel(user)}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">{getRoleBadge(user.role)}</td>
                    <td className="px-6 py-4">{getStatusBadge(user.status)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`user-actions-${user.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {user.status === 'pending_verification' && user.email && (
                            <DropdownMenuItem onClick={() => handleResendSetup(user.id, user.email)}>
                              <Mail className="h-4 w-4 mr-2" />
                              Resend Setup Email
                            </DropdownMenuItem>
                          )}
                          {user.status === 'active' && user.email && (
                            <DropdownMenuItem onClick={() => handleForcePasswordReset(user.id, user.email)}>
                              <Key className="h-4 w-4 mr-2" />
                              Force Password Reset
                            </DropdownMenuItem>
                          )}
                          {user.role === 'dependant' && (
                            <DropdownMenuItem onClick={() => handleResetDependantPassword(user.id)}>
                              <Key className="h-4 w-4 mr-2" />
                              Reset Password (default)
                            </DropdownMenuItem>
                          )}
                          {user.status === 'active' && ['care_officer', 'supervisor'].includes(user.role) && (
                            <DropdownMenuItem onClick={() => handleSuspend(user.id)}>
                              <PauseCircle className="h-4 w-4 mr-2" />
                              Suspend
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {!['deactivated', 'suspended'].includes(user.status) ? (
                            <DropdownMenuItem 
                              onClick={() => handleDeactivate(user.id)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <UserX className="h-4 w-4 mr-2" />
                              Deactivate
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              onClick={() => handleReactivate(user.id)}
                              className="text-green-600 focus:text-green-600"
                            >
                              <UserCheck className="h-4 w-4 mr-2" />
                              Reactivate
                            </DropdownMenuItem>
                          )}
                          {currentUser?.id !== user.id && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setUserToDelete(user)}
                                className="text-red-700 focus:bg-red-50 focus:text-red-700"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Permanently
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete User Confirmation Modal */}
      <Dialog open={Boolean(userToDelete)} onOpenChange={(open) => !open && setUserToDelete(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-700">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <DialogTitle>Delete {getDisplayName(userToDelete)}?</DialogTitle>
            <DialogDescription>
              {userToDelete?.role === 'care_officer'
                ? 'This removes the care officer account and profile. Completed, submitted, flagged, rejected, approved, and evidence-backed visit history is preserved; future scheduled visits are cancelled for reassignment. This action cannot be undone.'
                : 'This permanently removes the account, role profile, KYC records, linked operational records, and stored uploads. This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>

          {userToDelete && (
            <div className="rounded-xl border border-red-100 bg-red-50/60 p-4 text-sm">
              <p className="font-medium text-red-950">{getDisplayName(userToDelete)}</p>
              <p className="mt-1 text-red-800">{getContactLabel(userToDelete)}</p>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setUserToDelete(null)}
              className="rounded-xl"
              disabled={Boolean(deletingUserId)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleDeleteUser}
              disabled={Boolean(deletingUserId)}
              className="rounded-xl bg-red-700 text-white hover:bg-red-800"
            >
              {deletingUserId ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete Permanently'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AdminUserDetailModal
        userId={detailUserId}
        open={Boolean(detailUserId)}
        onOpenChange={(open) => !open && setDetailUserId('')}
      />

      {/* Create User Modal */}
      <Dialog open={showCreateModal} onOpenChange={handleCreateModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Add a new user to the platform. They will receive an email to set up their password.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={newUser.first_name}
                  onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                  required
                  className="rounded-xl"
                  data-testid="create-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={newUser.last_name}
                  onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                  required
                  className="rounded-xl"
                  data-testid="create-last-name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                required
                className="rounded-xl"
                data-testid="create-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select 
                value={newUser.role} 
                onValueChange={(value) => setNewUser({...newUser, role: value})}
              >
                <SelectTrigger className="rounded-xl" data-testid="create-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="subscriber">Subscriber</SelectItem>
                  <SelectItem value="care_officer">Care Officer</SelectItem>
                  <SelectItem value="customer_care">Customer Care</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={newUser.phone}
                onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                required
                className="rounded-xl"
                data-testid="create-phone"
              />
            </div>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={newUser.country}
                  onChange={(e) => setNewUser({...newUser, country: e.target.value})}
                  className="rounded-xl"
                  data-testid="create-country"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={newUser.city}
                  onChange={(e) => setNewUser({...newUser, city: e.target.value})}
                  className="rounded-xl"
                  data-testid="create-city"
                />
              </div>
            </div>
            
            {(newUser.role === 'care_officer' || newUser.role === 'supervisor') && (
              <div className="space-y-2">
                <Label htmlFor="assigned_region">Assigned Region</Label>
                <Input
                  id="assigned_region"
                  value={newUser.assigned_region}
                  onChange={(e) => setNewUser({...newUser, assigned_region: e.target.value})}
                  placeholder="e.g., Lagos, Enugu"
                  className="rounded-xl"
                  data-testid="create-region"
                />
              </div>
            )}
            
            {newUser.role === 'care_officer' && (
              <div className="space-y-2">
                <Label htmlFor="performance_tier">Performance Tier</Label>
                <Select 
                  value={newUser.performance_tier} 
                  onValueChange={(value) => setNewUser({...newUser, performance_tier: value})}
                >
                  <SelectTrigger className="rounded-xl" data-testid="create-tier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Probation">Probation</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Senior">Senior</SelectItem>
                    <SelectItem value="Star">Star</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <DialogFooter className="mt-6">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => handleCreateModalOpen(false)}
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={createLoading}
                className="bg-burgundy-600 hover:bg-burgundy-700 rounded-xl"
                data-testid="create-user-submit"
              >
                {createLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create User'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
